import { RefObject, useEffect, useRef } from 'react';
import { capturePaysafeInstance } from '../utils/capturePaysafeInstance';
import type { CheckoutFormError } from './useCheckoutTracking';

const FIELD_STATE_ATTRIBUTE = 'data-checkout-v2-field';
const DISMISSED_ATTRIBUTE = 'data-checkout-v2-dismissed';
const REQUIRED_ATTRIBUTE = 'data-checkout-v2-required';
const FORM_SELECTOR = '.gc-dg-tab-content, #gc-dg-billing_address';
const BILLING_ADDRESS_SELECTOR = '#gc-dg-billing_address';
const PAYSAFE_FIELD_SELECTOR = 'div.gc-dg-paysafe-field';
const ERROR_MESSAGE_SELECTOR = '.gc-dg-error-message';
const REQUIRED_MESSAGE_CLASS = 'checkout-v2__required-error';
const SUMMARY_SELECTOR = '#gc-dg-validationSummary';
const SUBMIT_SELECTOR = '#gc-dg-payNow';
const SAVE_SELECTOR = '.gc-dg-btn-save';
const CARD_FORM_SELECTOR = '#gc-dg-card.active';
const LOADER_SELECTOR = '#gc-dg-Loader';
const TERMS_CHECKBOX_SELECTOR = "input[type='checkbox'][id^='gc-dg-tandc-']";
const TERMS_ERROR_SELECTOR = "[id^='gc-dg-termsAndConditionsError-']";

const PAYSAFE_IFRAME_FIELDS = 'cardNumber expiryDate cvv';

const ADDRESS_REQUIRED_MESSAGES: Record<string, string> = {
  street: 'Street address is required.',
  postalCode: 'Zip code is required.',
  stateCode: 'State code is required.',
  city: 'City is required.',
};

type FieldState = 'success' | 'error';
type FieldValidation = { state?: FieldState; error?: Omit<CheckoutFormError, 'errorField'> };
type PaysafeFieldEvent = {
  type: string;
  target: { containerElement: HTMLElement };
  data: { isEmpty: boolean };
};
type PaysafeInstance = {
  fields: (names: string) => {
    on: (events: string, listener: (instance: unknown, event: PaysafeFieldEvent) => void) => void;
  };
};

// A Set, as the billing address is nested in a payment form once placed there
const findFields = (panel: HTMLElement) =>
  new Set(
    Array.from(panel.querySelectorAll<HTMLElement>(FORM_SELECTOR)).flatMap((form) => [
      ...form.querySelectorAll<HTMLElement>(PAYSAFE_FIELD_SELECTOR),
      ...Array.from(form.querySelectorAll<HTMLInputElement>("input[type='text']")).filter(
        (input) => !input.closest(PAYSAFE_FIELD_SELECTOR),
      ),
    ]),
  );

const toField = (input: HTMLInputElement) =>
  input.closest<HTMLElement>(PAYSAFE_FIELD_SELECTOR) ?? input;

const findInput = (field: HTMLElement) =>
  field instanceof HTMLInputElement ? field : field.querySelector('input');

// The state input sits in an autocomplete wrapper, which its messages follow
const findMessageAnchor = (field: HTMLElement) =>
  field.closest('.gc-dg-autocomplete-container') ?? field;

// The payment forms toggle their messages with gc-dg-hidden, the billing address form renders them
const findErrorMessage = (field: HTMLElement) => {
  let next = findMessageAnchor(field).nextElementSibling;
  if (next?.classList.contains(REQUIRED_MESSAGE_CLASS)) next = next.nextElementSibling;
  return next?.matches(ERROR_MESSAGE_SELECTOR) ? (next as HTMLElement) : null;
};

// The SDK ids name the fields, e.g. gc-dg-routingNumber or gc-dg-form-street
const getFieldName = (field: HTMLElement) =>
  (field.id || findInput(field)?.id || '').replace(/^gc-dg-(form-)?/, '');

const isShown = (message: HTMLElement | null): message is HTMLElement =>
  !!message &&
  !message.classList.contains('gc-dg-hidden') &&
  !message.hasAttribute(DISMISSED_ATTRIBUTE);

/**
 * @description Success and error states of the payment and billing address fields, as on the
 * personal details form. The SDK only validates on submit or save, so an SDK error is dismissed once
 * the field is edited, and a required field emptied by the customer shows an error on blur, as does a
 * card field left with a value Paysafe doesn't report valid.
 * A dismissed error shows again once the SDK reports it again. The SDK doesn't clear the card form
 * errors of the last submit when the next one passes, so all errors are dismissed once the payment
 * starts.
 * On a card submit the SDK checks the card fields only once the card holder name and the consent box
 * pass, so the card fields not valid yet and an unticked consent box show their errors on the submit,
 * as all the bank account errors do.
 * Paysafe iframe values can't be read, so their validity comes from the Paysafe field events.
 * onFieldError is called each time a field turns invalid.
 */
export const useFieldValidationState = (
  panelRef: RefObject<HTMLElement | null>,
  onFieldError?: (error: CheckoutFormError) => void,
) => {
  const onFieldErrorRef = useRef(onFieldError);
  onFieldErrorRef.current = onFieldError;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const paysafeValidity = new WeakMap<Element, boolean>();
    const paysafeEmptiness = new WeakMap<Element, boolean>();
    const editedFields = new WeakSet<HTMLElement>();
    const requiredFields = new WeakSet<HTMLElement>();
    const invalidFields = new Set<HTMLElement>();
    const submittedFields = new Set<HTMLElement>();
    const describedFields = new WeakSet<HTMLElement>();
    const addressMessages = new Map<HTMLElement, HTMLElement>();
    const fieldStates = new WeakMap<HTMLElement, FieldState | undefined>();

    const isEmpty = (field: HTMLElement) => {
      const input = findInput(field);
      return input ? input.value === '' : paysafeEmptiness.get(field) !== false;
    };

    const isValid = (field: HTMLElement) =>
      findInput(field) ? !isEmpty(field) : paysafeValidity.get(field) === true;

    const getAddressMessage = (field: HTMLElement) => {
      const input = findInput(field);
      return field.closest(BILLING_ADDRESS_SELECTOR) && input
        ? ADDRESS_REQUIRED_MESSAGES[input.name]
        : undefined;
    };

    const canRequire = (field: HTMLElement) =>
      field.closest(BILLING_ADDRESS_SELECTOR)
        ? !!getAddressMessage(field)
        : !!findErrorMessage(field);

    const showRequiredMessage = (field: HTMLElement) => {
      const text = getAddressMessage(field);
      let message: HTMLElement | null | undefined;
      if (text) {
        message = addressMessages.get(field);
        if (!message) {
          message = document.createElement('div');
          message.className = `gc-dg-error-message ${REQUIRED_MESSAGE_CLASS}`;
          message.id = `${field.id}-required-error`;
          message.textContent = text;
          findMessageAnchor(field).after(message);
          addressMessages.set(field, message);
        }
      } else {
        message = findErrorMessage(field);
        message?.removeAttribute(DISMISSED_ATTRIBUTE);
        message?.setAttribute(REQUIRED_ATTRIBUTE, '');
      }
      if (!message) return;
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', message.id);
      describedFields.add(field);
    };

    const hideRequiredMessage = (field: HTMLElement, hasSdkError: boolean) => {
      addressMessages.get(field)?.remove();
      addressMessages.delete(field);
      if (!getAddressMessage(field)) findErrorMessage(field)?.removeAttribute(REQUIRED_ATTRIBUTE);
      // An SDK error describes the field itself
      if (describedFields.has(field) && !hasSdkError) {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
      }
      describedFields.delete(field);
    };

    const getMessageText = (message: HTMLElement | null | undefined) =>
      message?.textContent?.trim() ?? '';

    const validate = (field: HTMLElement): FieldValidation => {
      const sdkMessage = findErrorMessage(field);
      const hasSdkError = isShown(sdkMessage);
      if (requiredFields.has(field) && !isEmpty(field)) requiredFields.delete(field);
      if (invalidFields.has(field) && isValid(field)) invalidFields.delete(field);
      if (submittedFields.has(field) && isValid(field)) submittedFields.delete(field);
      const isRequired = requiredFields.has(field);
      const isSubmitted = submittedFields.has(field);
      const hasRequiredError =
        (isRequired || isSubmitted || invalidFields.has(field)) && !hasSdkError;
      if (hasRequiredError) showRequiredMessage(field);
      else hideRequiredMessage(field, hasSdkError);
      if (hasSdkError) {
        return {
          state: 'error',
          error: { errorMessage: getMessageText(sdkMessage), errorSource: 'submit' },
        };
      }
      if (hasRequiredError) {
        const errorMessage = getAddressMessage(field) ?? getMessageText(sdkMessage);
        const errorSource = isRequired ? 'required' : isSubmitted ? 'submit' : 'invalid';
        return { state: 'error', error: { errorMessage, errorSource } };
      }
      return { state: isValid(field) ? 'success' : undefined };
    };

    const update = () => {
      // The address form drops its fields when the edit is cancelled or saved
      addressMessages.forEach((message, field) => {
        if (field.isConnected) return;
        message.remove();
        addressMessages.delete(field);
      });
      let hasError = false;
      findFields(panel).forEach((field) => {
        const { state, error } = validate(field);
        if (state === 'error') hasError = true;
        if (error && fieldStates.get(field) !== 'error') {
          onFieldErrorRef.current?.({ errorField: getFieldName(field), ...error });
        }
        fieldStates.set(field, state);
        if (state) field.setAttribute(FIELD_STATE_ATTRIBUTE, state);
        else field.removeAttribute(FIELD_STATE_ATTRIBUTE);
      });
      const summary = panel.querySelector(SUMMARY_SELECTOR);
      if (hasError) summary?.removeAttribute(DISMISSED_ATTRIBUTE);
      else summary?.setAttribute(DISMISSED_ATTRIBUTE, '');
    };

    // The same attributes the SDK clears along with an error it no longer reports
    const dismissError = (field: HTMLElement) => {
      const message = findErrorMessage(field);
      if (!isShown(message)) return;
      message.setAttribute(DISMISSED_ATTRIBUTE, '');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    };

    const dismissAllErrors = () => {
      invalidFields.clear();
      submittedFields.clear();
      panel
        .querySelectorAll(TERMS_ERROR_SELECTOR)
        .forEach((message) => message.removeAttribute(REQUIRED_ATTRIBUTE));
      findFields(panel).forEach(dismissError);
      panel.querySelectorAll<HTMLElement>(ERROR_MESSAGE_SELECTOR).forEach((message) => {
        if (isShown(message)) message.setAttribute(DISMISSED_ATTRIBUTE, '');
      });
    };

    const handleMutations = (records: MutationRecord[]) => {
      records.forEach(({ target, attributeName }) => {
        if (!(target instanceof HTMLElement)) return;
        if (
          attributeName === 'role' &&
          target.matches(ERROR_MESSAGE_SELECTOR) &&
          target.hasAttribute('role')
        ) {
          target.removeAttribute(DISMISSED_ATTRIBUTE);
        }
        if (
          attributeName === 'class' &&
          target.matches(LOADER_SELECTOR) &&
          !target.classList.contains('gc-dg-hidden')
        ) {
          dismissAllErrors();
        }
      });
      update();
    };

    const observer = new MutationObserver(handleMutations);

    const catchUpWithSdk = () => handleMutations(observer.takeRecords());

    const requireIfEmptied = (field: HTMLElement) => {
      if (editedFields.has(field) && isEmpty(field) && canRequire(field)) requiredFields.add(field);
    };

    const isTextInput = (target: EventTarget | null): target is HTMLInputElement =>
      target instanceof HTMLInputElement && target.type === 'text';

    const handleInput = (event: Event) => {
      catchUpWithSdk();
      if (isTextInput(event.target)) {
        const field = toField(event.target);
        editedFields.add(field);
        dismissError(field);
      }
      update();
    };

    // Blur does not bubble, so the panel listens to focusout
    const handleFocusOut = (event: FocusEvent) => {
      if (!isTextInput(event.target)) return;
      requireIfEmptied(toField(event.target));
      update();
    };

    const stopCapturingPaysafe = capturePaysafeInstance((instance) => {
      (instance as PaysafeInstance)
        .fields(PAYSAFE_IFRAME_FIELDS)
        // Valid and Invalid only come when the validity changes, FieldValueChange on every edit
        .on(
          'Valid Invalid FieldValueChange Blur',
          (_, { type, target: { containerElement }, data }) => {
            catchUpWithSdk();
            paysafeEmptiness.set(containerElement, data.isEmpty);
            if (type === 'Blur') {
              requireIfEmptied(containerElement);
              if (editedFields.has(containerElement) && !isEmpty(containerElement)) {
                invalidFields.add(containerElement);
              }
            } else {
              editedFields.add(containerElement);
              invalidFields.delete(containerElement);
              submittedFields.delete(containerElement);
              dismissError(containerElement);
            }
            if (type === 'Valid' || type === 'Invalid') {
              paysafeValidity.set(containerElement, type === 'Valid');
            }
            update();
          },
        );
    });

    const handleChange = (event: Event) => {
      const checkbox = event.target as HTMLInputElement;
      if (!checkbox.matches(TERMS_CHECKBOX_SELECTOR) || !checkbox.checked) return;
      catchUpWithSdk();
      const message = checkbox
        .closest('.gc-dg-tab-content')
        ?.querySelector<HTMLElement>(TERMS_ERROR_SELECTOR);
      message?.removeAttribute(REQUIRED_ATTRIBUTE);
      if (isShown(message ?? null)) message?.setAttribute(DISMISSED_ATTRIBUTE, '');
      update();
    };

    const requireCardForm = () => {
      const form = panel.querySelector(CARD_FORM_SELECTOR);
      if (!form) return;
      form.querySelectorAll<HTMLElement>(PAYSAFE_FIELD_SELECTOR).forEach((field) => {
        if (!findInput(field) && !isValid(field)) submittedFields.add(field);
      });
      const terms = form.querySelector<HTMLInputElement>(TERMS_CHECKBOX_SELECTOR);
      const termsMessage = form.querySelector<HTMLElement>(TERMS_ERROR_SELECTOR);
      if (!terms || terms.checked || !termsMessage) return;
      termsMessage.removeAttribute(DISMISSED_ATTRIBUTE);
      termsMessage.setAttribute(REQUIRED_ATTRIBUTE, '');
    };

    // Bubble phase, so a CTA click usePaymentMethodSelection blocks requires nothing
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target.closest(SUBMIT_SELECTOR)) {
        requireCardForm();
      } else if (target.closest(SAVE_SELECTOR)) {
        panel
          .querySelectorAll(`${BILLING_ADDRESS_SELECTOR} [${DISMISSED_ATTRIBUTE}]`)
          .forEach((message) => message.removeAttribute(DISMISSED_ATTRIBUTE));
      } else {
        return;
      }
      update();
    };

    observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'role'],
    });
    panel.addEventListener('input', handleInput);
    panel.addEventListener('focusout', handleFocusOut);
    panel.addEventListener('change', handleChange);
    panel.addEventListener('click', handleClick);
    update();

    return () => {
      stopCapturingPaysafe();
      observer.disconnect();
      panel.removeEventListener('input', handleInput);
      panel.removeEventListener('focusout', handleFocusOut);
      panel.removeEventListener('change', handleChange);
      panel.removeEventListener('click', handleClick);
      addressMessages.forEach((message) => message.remove());
    };
  }, [panelRef]);
};
